import { useState } from "react";
import Accordion from "react-bootstrap/Accordion";
import dayjs from "dayjs";
import { useFopLabels } from "../hooks/useFopLabels";

const LIMIT = 200_000;
const USD_CARD_LIMIT = 50_000;

const fmt = (v) =>
    Number(v || 0).toLocaleString("uk-UA", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });


export default function ForeignSpendAccordion({ limits, user, currency, usdSellRate }) {
    const { getLabel } = useFopLabels();
    const [limitTx, setLimitTx] = useState({});
    const [loadingTx, setLoadingTx] = useState(false);
    const [usdCardTx, setUsdCardTx] = useState({});
    const [loadingUsdCardTx, setLoadingUsdCardTx] = useState(false);
    const isAdmin = user?.role === "admin";

    const loadLimitTx = async (fop) => {
        if (limitTx[fop]) return;

        setLoadingTx(true);

        const res = await fetch(
            `/api/limits/foreign-spend/${encodeURIComponent(fop)}`
        );
        const data = await res.json();

        setLimitTx((prev) => ({
            ...prev,
            [fop]: Array.isArray(data) ? data : [],
        }));

        setLoadingTx(false);
    };

    const loadUsdCardTx = async (fop) => {
        if (usdCardTx[fop]) return;

        setLoadingUsdCardTx(true);

        const res = await fetch(
            `/api/limits/foreign-spend/${encodeURIComponent(fop)}/usd-card`
        );
        const data = await res.json();

        setUsdCardTx((prev) => ({
            ...prev,
            [fop]: Array.isArray(data) ? data : [],
        }));

        setLoadingUsdCardTx(false);
    };
    const toUsd = (uah) => {
        if (!usdSellRate) return null;
        return uah / usdSellRate;
    };

    const getFopName = (fop) => getLabel(fop) || `ФОП ${fop}`;


    if (!limits || limits.length === 0) return null;

    const formatMoney = (uah) => {
        if (currency === "USD" && usdSellRate) {
            return `$${fmt(uah / usdSellRate)}`;
        }
        return `${fmt(uah)} грн`;
    };

    return (
        <>
        <Accordion className="lava-accordion mb-4" alwaysOpen>
            <h3 className="mb-3">🌍 Закордонні витрати (ліміт НБУ)</h3>

            {limits.map((l, i) => {
                const percent = Math.min(100, ((l.spent || 0) / LIMIT) * 100);

                const header = (
                    <div style={{ width: "100%" }}>
                        <div className="d-flex justify-content-between align-items-center">
                            <span className="text-muted">UAH картки</span>

                            <span className="small text-muted text-end">
                {fmt(l.spent)} / {LIMIT.toLocaleString()} грн
                {currency === "UAH" && usdSellRate ? (
                    <span className="ms-1">
                      (~${fmt(toUsd(l.spent))})
                    </span>
                ) : null}
              </span>
                        </div>

                        {/* PROGRESS */}
                        <div className="progress mt-2" style={{ height: 8 }}>
                            <div
                                className={`progress-bar ${
                                    l.remaining < 5000
                                        ? "bg-danger"
                                        : l.remaining < 20000
                                            ? "bg-warning"
                                            : "bg-success"
                                }`}
                                style={{ width: `${percent}%` }}
                            />
                        </div>
                        <div className="small mt-1">
                            <span className="text-muted">Залишилось:</span> <strong>{formatMoney(l.remaining)}</strong>

                            {currency === "UAH" && usdSellRate && (
                                <span className="ms-1 text-muted">
                                  (~${fmt(toUsd(l.remaining))})
                                </span>
                            )}
                        </div>
                    </div>
                );

                const usdLimit = l.usdLimit || USD_CARD_LIMIT;
                // Скільки грн пішло цього місяця на купівлю валюти — саме це обмежене лімітом НБУ.
                const usdTopUpUah = l.usdTopUpUah || 0;
                const usdTopUpRemaining = Math.max(0, usdLimit - usdTopUpUah);
                const usdTopUpPercent = Math.min(100, (usdTopUpUah / usdLimit) * 100);

                // Скільки з уже купленої валюти реально можна ще витратити (у $, без будь-якого курсу).
                const usdRemainingUsd = l.usdRemainingUsd ?? 0;
                const usdRemainingUah = usdSellRate ? usdRemainingUsd * usdSellRate : null;

                const usdHeader = (
                    <div style={{ width: "100%" }}>
                        <div className="d-flex justify-content-between align-items-center">
                            <span className="text-muted">USD картка</span>

                            <span className="small text-muted text-end">
                {fmt(usdTopUpUah)} / {usdLimit.toLocaleString()} грн
              </span>
                        </div>

                        <div className="progress mt-2" style={{ height: 8 }}>
                            <div
                                className={`progress-bar ${
                                    usdTopUpRemaining < 2500
                                        ? "bg-danger"
                                        : usdTopUpRemaining < 10000
                                            ? "bg-warning"
                                            : "bg-success"
                                }`}
                                style={{ width: `${usdTopUpPercent}%` }}
                            />
                        </div>
                        <div className="small mt-1">
                            <span className="text-muted">Залишилось:</span> <strong>${fmt(usdRemainingUsd)}</strong>

                            {usdRemainingUah != null && (
                                <span className="ms-1 text-muted">
                  (~{fmt(usdRemainingUah)} грн)
                </span>
                            )}
                        </div>
                    </div>
                );

                const usdBlock = !isAdmin ? (
                    <div className="accordion-item h-100">
                        <h2 className="accordion-header">
                            <div className="accordion-button collapsed accordion-button--static">
                                {usdHeader}
                            </div>
                        </h2>
                    </div>
                ) : (
                    <Accordion.Item
                        className="h-100"
                        eventKey={`usd-${i}`}
                        onClick={() => loadUsdCardTx(l.fop)}
                    >
                        <Accordion.Header>
                            {usdHeader}
                        </Accordion.Header>

                        <Accordion.Body>
                            {loadingUsdCardTx && !usdCardTx[l.fop] ? (
                                <div className="text-muted">Завантаження…</div>
                            ) : (
                                <table className="table table-sm">
                                    <thead>
                                    <tr>
                                        <th>Дата та опис</th>
                                        <th className="text-end" style={{ width: "1%", whiteSpace: "nowrap" }}>Сума</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {(usdCardTx[l.fop] || []).map((tx) => {
                                        const usd = Math.abs(tx.amount || 0) / 100;
                                        return (
                                        <tr key={tx._id}>
                                            <td>
                                                <div>
                                                    {tx.time
                                                        ? dayjs(tx.time).format("DD.MM.YYYY")
                                                        : "—"}
                                                </div>
                                                <div className="text-muted small mt-1">
                                                    {tx.description?.trim() ||
                                                        tx.comment?.trim() ||
                                                        "—"}
                                                </div>
                                            </td>
                                            <td className="text-end align-middle">
                                                <div>${fmt(usd)}</div>
                                                {usdSellRate ? (
                                                    <div className="text-muted small">
                                                        (~{fmt(usd * usdSellRate)} грн)
                                                    </div>
                                                ) : null}
                                            </td>
                                        </tr>
                                    );})}

                                    {usdCardTx[l.fop]?.length === 0 && (
                                        <tr>
                                            <td colSpan={2} className="text-muted text-start">
                                                Немає транзакцій
                                            </td>
                                        </tr>
                                    )}
                                    </tbody>
                                </table>
                            )}
                        </Accordion.Body>
                    </Accordion.Item>
                );

                const mainBlock = !isAdmin ? (
                    <div className="accordion-item">
                        <h2 className="accordion-header">
                            <div className="accordion-button collapsed accordion-button--static">
                                {header}
                            </div>
                        </h2>
                    </div>
                ) : (
                    <Accordion.Item
                        eventKey={String(i)}
                        onClick={() => loadLimitTx(l.fop)}
                    >
                        <Accordion.Header>
                            {header}
                        </Accordion.Header>

                        <Accordion.Body>
                            {loadingTx && !limitTx[l.fop] ? (
                                <div className="text-muted">Завантаження…</div>
                            ) : (
                                <table className="table table-sm">
                                    <thead>
                                    <tr>
                                        <th>Дата та опис</th>
                                        <th className="text-end" style={{ width: "1%", whiteSpace: "nowrap" }}>Сума</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {(limitTx[l.fop] || []).map((tx) => {
                                        const uah = Math.abs(tx.amount || 0) / 100;
                                        return (
                                        <tr key={tx._id}>
                                            <td>
                                                <div>
                                                    {tx.time
                                                        ? dayjs(tx.time).format("DD.MM.YYYY")
                                                        : "—"}
                                                </div>
                                                <div className="text-muted small mt-1">
                                                    {tx.description?.trim() ||
                                                        tx.comment?.trim() ||
                                                        "—"}
                                                </div>
                                            </td>
                                            <td className="text-end align-middle">
                                                <div>
                                                    {currency === "USD" && usdSellRate
                                                        ? `$${fmt(uah / usdSellRate)}`
                                                        : `${fmt(uah)} грн`}
                                                </div>
                                                {currency === "UAH" && usdSellRate ? (
                                                    <div className="text-muted small">
                                                        (~${fmt(toUsd(uah))})
                                                    </div>
                                                ) : null}
                                            </td>
                                        </tr>
                                    );})}

                                    {limitTx[l.fop]?.length === 0 && (
                                        <tr>
                                            <td colSpan={2} className="text-muted text-start">
                                                Немає транзакцій
                                            </td>
                                        </tr>
                                    )}
                                    </tbody>
                                </table>
                            )}
                        </Accordion.Body>
                    </Accordion.Item>
                );

                return (
                    <div key={l.fop} className="foreign-spend-fop-card mb-3">
                        <div className="foreign-spend-fop-title">{getFopName(l.fop)}</div>
                        <div className="row g-3 align-items-stretch">
                            <div className="col-12 col-lg-8">{mainBlock}</div>
                            <div className="col-12 col-lg-4">{usdBlock}</div>
                        </div>
                    </div>
                );
            })}
        </Accordion>
        </>
    );
}
